import React from 'react';
import { AiService } from '../../../services/axios/axios-api-v2-generated';
import { UdViewModel } from '../../../udView/udview';
import { useAppDispatch, useAppSelector, useAppStore } from '../../hooks/store-hooks';
import { RootState } from '../../state/slices';
import { aiChatActions } from '../../state/slices/ai-chat-slice';
import { patchFocusedWorkspaceView } from '../../state/store';
import {
    AI_PROMPT_MAX_LENGTH,
    buildConversationHistory,
    hasChanges,
    parseResponseKind,
    planChanges,
    revertChanges,
    VIEW_CHANGED_MESSAGE
} from './ai-chat-helpers';
import { filterToApi } from "../../state/slices/views-slice";

const selectFocusedView = (state: RootState): UdViewModel | undefined => state.workspace.items[state.workspace.selectedId]?.view;

export function useAiChat() {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const chat = useAppSelector((state) => state.aiChat);
    const view = useAppSelector(selectFocusedView);
    const requestSeq = React.useRef(0);

    // Switching views drops the conversation and orphans any in-flight response
    const viewId = view?.id;
    React.useEffect(() => {
        requestSeq.current++;
        dispatch(aiChatActions.clear());
    }, [viewId, dispatch]);

    const send = React.useCallback(
        async (text: string, isRetry = false) => {
            const prompt = text.trim().slice(0, AI_PROMPT_MAX_LENGTH);
            const state = store.getState();
            const snapshot = selectFocusedView(state);
            if (!prompt || !snapshot || state.aiChat.loading) {
                return;
            }
            // A retry reuses the trailing user message, so it must not appear twice in the history
            const history = buildConversationHistory(isRetry ? state.aiChat.messages.slice(0, -1) : state.aiChat.messages);
            const snapshotFilters = snapshot.plane.filter.planeFilterSettings;
            const seq = ++requestSeq.current;
            dispatch(isRetry ? aiChatActions.retryStarted() : aiChatActions.userMessage(prompt));
            try {
                const response = await AiService.viewChanges(
                    {
                        body: {
                            prompt,
                            conversationHistory: history,
                            viewSnapshot: { name: snapshot.name, filters: snapshotFilters.map(filterToApi) } 
                        }
                    },
                    { withAuthorization: true }
                );
                const current = selectFocusedView(store.getState());
                if (seq !== requestSeq.current || current?.id !== snapshot.id) {
                    return;
                }
                const text = response.message ?? '';
                const kind = parseResponseKind(response.kind);
                if (kind === 'clarification' && response.clarification) {
                    const { question, suggestedAnswers } = response.clarification;
                    dispatch(
                        aiChatActions.assistantMessage({
                            text: text || question || '',
                            kind,
                            clarification: { question: question ?? '', suggestedAnswers: suggestedAnswers ?? [] }
                        })
                    );
                    return;
                }
                if (kind !== 'changes') {
                    dispatch(aiChatActions.assistantMessage({ text, kind: 'reply' }));
                    return;
                }
                const plan = planChanges(response, snapshotFilters, current.plane.filter.planeFilterSettings);
                if (!plan.ok) {
                    dispatch(aiChatActions.assistantMessage({ text: VIEW_CHANGED_MESSAGE, kind: 'reply' }));
                    return;
                }
                if (!hasChanges(plan.changes)) {
                    dispatch(aiChatActions.assistantMessage({ text, kind: 'reply' }));
                    return;
                }
                dispatch(
                    patchFocusedWorkspaceView((v) => {
                        v.plane.filter.planeFilterSettings = plan.filters;
                        if (plan.changes.added.length > 0) {
                            v.plane.filter.useFilters = true;
                        }
                    })
                );
                dispatch(aiChatActions.assistantMessage({ text, kind: 'changes', changes: plan.changes }));
            } catch {
                if (seq === requestSeq.current) {
                    dispatch(aiChatActions.failed('Something went wrong while asking the AI. Please try again.'));
                }
            }
        },
        [store, dispatch]
    );

    const retry = React.useCallback(() => {
        const messages = store.getState().aiChat.messages;
        const last = messages[messages.length - 1];
        if (last?.role === 'user') {
            send(last.text, true);
        }
    }, [store, send]);

    const revert = React.useCallback(
        (messageId: number) => {
            const state = store.getState();
            const message = state.aiChat.messages.find((m) => m.id === messageId);
            const current = selectFocusedView(state);
            if (!message?.changes || message.reverted || !current) {
                return;
            }
            const next = revertChanges(current.plane.filter.planeFilterSettings, message.changes);
            if (!next) {
                dispatch(aiChatActions.failed('These filters were changed since, so this change can no longer be reverted.'));
                return;
            }
            dispatch(
                patchFocusedWorkspaceView((v) => {
                    v.plane.filter.planeFilterSettings = next;
                })
            );
            dispatch(aiChatActions.markReverted(messageId));
        },
        [store, dispatch]
    );

    return { chat, filters: view?.plane.filter.planeFilterSettings, send, retry, revert };
}
